import { createSession, deleteSession, startInteraction, pollInteraction } from './js/portal/gaia-chat';
import { get_feature_flag_property } from './js/portal/feature_flags';
import {
  createState,
  MessageBubble,
  ErrorBubble,
  TypingIndicator,
  handlePollResult,
  createChatHelpers,
} from './js/portal/rai-chat-utils';
import bmwLogoSrc from '../images/bmw-logo-new-white.svg';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SESSION_KEY = 'gaia_session_id';
const PANEL_OPEN_KEY = 'gaia_panel_open';
const MESSAGES_KEY = 'gaia_chat_messages';
const CHAT_HISTORY_KEY = 'gaia_chat_history';
const PENDING_KEY = 'gaia_pending_interaction';
const PANEL_WIDTH_KEY = 'gaia_panel_width';
const TRIGGER_POS_KEY = 'gaia_trigger_pos';
const BUBBLE_DISMISSED_KEY = 'gaia_bubble_dismissed';
const DEFAULT_PANEL_PCT = 25;
const MIN_PANEL_PCT = 15;
const MAX_PANEL_PCT = 50;

// ---------------------------------------------------------------------------
// Main widget component
// ---------------------------------------------------------------------------

export default function GaiaChatWidget() {
  // ---- Feature flag gate: hide widget until flag is confirmed enabled ----
  const wrapper = <div class="gaia-feature-flag-wrapper" style="display:none" />;

  get_feature_flag_property('gaia-chatbot.enabled', false)
    .then(enabled => {
      if (enabled) {
        wrapper.style.display = '';
        wrapper.appendChild(buildWidget());
        // Hide widget on the dedicated rAI Chat page
        function syncVisibility() {
          wrapper.style.display = window.location.hash === '#raichat' ? 'none' : '';
        }
        syncVisibility();
        window.addEventListener('hashchange', syncVisibility);
      }
    })
    .catch(err => {
      console.warn('GAIA chatbot feature flag check failed, hiding widget:', err);
    });

  return wrapper;
}

function buildWidget() {
  // ---- state (messages & chatHistory are restored from sessionStorage) ----
  const savedMessages = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(MESSAGES_KEY)) || [];
    } catch {
      return [];
    }
  })();
  const savedChatHistory = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(CHAT_HISTORY_KEY)) || [];
    } catch {
      return [];
    }
  })();
  const messages = createState(savedMessages);
  const chatHistory = createState(savedChatHistory);
  const loading = createState(false);
  const panelOpen = createState(sessionStorage.getItem(PANEL_OPEN_KEY) === 'true');
  const panelWidth = createState(parseInt(sessionStorage.getItem(PANEL_WIDTH_KEY), 10) || DEFAULT_PANEL_PCT);

  // Persist messages & chatHistory on every change
  messages.subscribe(v => {
    try {
      sessionStorage.setItem(MESSAGES_KEY, JSON.stringify(v));
    } catch {
      /* quota exceeded — non-critical */
    }
  });
  chatHistory.subscribe(v => {
    try {
      sessionStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(v));
    } catch {
      /* quota exceeded — non-critical */
    }
  });
  let abortController = null;
  let lastFailedText = null; // remember the user text that caused an error for retry

  const chatRefs = {
    messagesEl: null,
    scrollPillEl: null,
    typingEl: null,
    inputEl: null,
    sendBtnEl: null,
    isVisible: () => panelOpen.get(),
  };
  const {
    scrollToBottom,
    updateScrollPill,
    hideEmptyState,
    appendMessage,
    appendStreamingBubble,
    showTyping,
    hideTyping,
    setInputEnabled,
  } = createChatHelpers(chatRefs);

  // ---- DOM refs (assigned after JSX tree is built) ----
  let messagesEl;
  let inputEl;
  let panelEl;
  let triggerEl;
  let triggerTextEl;
  let bodyEl;
  let typingEl;
  let sendBtnEl;
  let sessionBtnEl;
  let scrollPillEl;

  // ---- helpers ----

  async function ensureSession() {
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      const res = await createSession();
      sessionId = res.sessionId;
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  }

  // ---- actions ----

  function appendErrorBubble(text, showRetry = true) {
    hideEmptyState();
    const errorEl = <ErrorBubble text={text} showRetry={showRetry} />;
    messagesEl.appendChild(errorEl);
    if (showRetry) {
      const retryBtn = errorEl.querySelector('.gaia-retry-btn');
      if (retryBtn) {
        retryBtn.addEventListener('click', () => retryLastMessage());
      }
    }
    scrollToBottom();
    return errorEl;
  }

  function appendContentFilteredError(errorText, blockedUserText) {
    hideEmptyState();
    const displayText =
      errorText ||
      'Your message was blocked by content moderation. You can continue chatting or start a new conversation.';
    const errorEl = (
      <div class="gaia-msg gaia-msg--ai gaia-msg--error">
        <i class="gaia-msg__icon gaia-msg__icon--error fas fa-exclamation-circle" />
        <div class="gaia-msg__bubble gaia-msg__bubble--error">
          <span class="gaia-error__text">{displayText}</span>
          {blockedUserText && (
            <details class="gaia-blocked-details">
              <summary>Show blocked message</summary>
              <pre class="gaia-blocked-text">{blockedUserText}</pre>
            </details>
          )}
          <button class="gaia-retry-btn" data-tooltip="Start new conversation">
            <i class="fas fa-trash-alt" />
            <span>New Conversation</span>
          </button>
        </div>
      </div>
    );
    messagesEl.appendChild(errorEl);
    const btn = errorEl.querySelector('.gaia-retry-btn');
    if (btn) btn.addEventListener('click', () => handleNewSession());
    scrollToBottom();
    return errorEl;
  }

  function removeErrorBubbles() {
    const errorBubbles = messagesEl?.querySelectorAll('.gaia-msg--error');
    if (errorBubbles) errorBubbles.forEach(el => el.remove());
    // Also remove error entries from state
    messages.set(prev => prev.filter(m => m.role !== 'error'));
  }

  function retryLastMessage() {
    if (!lastFailedText || loading.get()) return;
    const text = lastFailedText;
    lastFailedText = null;

    // Remove the error bubble and the failed streaming bubble from the DOM
    removeErrorBubbles();
    // Remove the last AI bubble (may contain partial streamed text from the failed attempt)
    const orphanStreaming = messagesEl?.querySelectorAll('.gaia-msg--ai:not(.gaia-msg--error)');
    if (orphanStreaming?.length) {
      orphanStreaming[orphanStreaming.length - 1].remove();
    }
    // Also remove the last AI entry from messages state if present
    messages.set(prev => {
      const lastAiIdx = prev.findLastIndex(m => m.role === 'ai');
      if (lastAiIdx >= 0) return [...prev.slice(0, lastAiIdx), ...prev.slice(lastAiIdx + 1)];
      return prev;
    });

    sendUserMessage(text, true);
  }

  async function handleSend() {
    const text = inputEl?.value?.trim();
    if (!text || loading.get()) return;

    inputEl.value = '';
    // If retrying, clear any leftover error state
    removeErrorBubbles();
    sendUserMessage(text, false);
  }

  async function sendUserMessage(text, isRetry = false) {
    const userTime = new Date().toISOString();
    if (!isRetry) {
      messages.set(prev => [...prev, { role: 'user', text, time: userTime }]);
      appendMessage('user', text, userTime);
    }

    loading.set(true);
    setInputEnabled(false);
    showTyping();

    try {
      const sessionId = await ensureSession();
      abortController = new AbortController();

      // Send the full chatHistory (human + AI messages) so GAIA has complete
      // conversation context. Previously we sent human-only history which caused
      // GAIA to produce verbose/repetitive responses.
      const options = {};
      const history = chatHistory.get();
      if (history.length > 0) {
        options.chatHistory = history;
      }

      // Start the interaction and get the interactionId
      const startResult = await startInteraction(sessionId, text, options);
      const { interactionId } = startResult;

      // Persist pending interaction so polling can resume after page reload
      sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          sessionId,
          interactionId,
          userText: text,
          startChatHistory: startResult.chatHistory || null,
        }),
      );

      // Prepare the streaming bubble but don't show it yet — keep the
      // typing dots visible until the first real text arrives from GAIA.
      let streamingStarted = false;
      const updateBubble = appendStreamingBubble();
      // Hide the streaming bubble initially; typing indicator is still visible
      const streamingWrapper = messagesEl.lastElementChild;
      streamingWrapper.style.display = 'none';

      const result = await pollInteraction(sessionId, interactionId, abortController.signal, partialText => {
        if (!streamingStarted) {
          streamingStarted = true;
          hideTyping();
          streamingWrapper.style.display = '';
        }
        updateBubble(partialText);
      });

      // Polling finished — clear persisted pending state
      sessionStorage.removeItem(PENDING_KEY);

      // Final render with the completed text (includes citations to strip)
      const {
        replyText,
        chatHistory: newHistory,
        isContentFiltered,
      } = handlePollResult(result, startResult.chatHistory);

      // If the response completed before any in_progress poll (fast answers),
      // the streaming bubble is still hidden — make it visible now.
      if (!streamingStarted) {
        hideTyping();
        streamingWrapper.style.display = '';
      }

      if (isContentFiltered) {
        // Remove the streaming bubble and the offending user message bubble from the DOM
        streamingWrapper.remove();
        const userBubbles = messagesEl?.querySelectorAll('.gaia-msg--user');
        if (userBubbles?.length) userBubbles[userBubbles.length - 1].remove();
        // Remove the offending user message from messages state, keep it for the error bubble
        const blockedText = text;
        messages.set(prev => {
          const lastUserIdx = prev.findLastIndex(m => m.role === 'user');
          if (lastUserIdx >= 0) return [...prev.slice(0, lastUserIdx), ...prev.slice(lastUserIdx + 1)];
          return prev;
        });
        messages.set(prev => [...prev, { role: 'error', text: replyText }]);
        appendContentFilteredError(replyText, blockedText);
      } else {
        updateBubble(replyText, true);
        messages.set(prev => [...prev, { role: 'ai', text: replyText, time: new Date().toISOString() }]);
      }

      // Update chatHistory for next turn
      if (newHistory) {
        chatHistory.set(newHistory);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;

      console.error('GAIA chat error:', err);
      sessionStorage.removeItem(PENDING_KEY);

      // Remove the streaming bubble left by the failed request (may have partial text)
      const streamBubble = messagesEl?.querySelector('.gaia-msg--ai:last-child');
      if (streamBubble && !streamBubble.classList.contains('gaia-msg--error')) {
        streamBubble.remove();
      }

      // Remember the user text so the retry button can re-send it
      lastFailedText = text;

      // If session expired / not found, reset and let the user try again
      if (err.response?.status === 404) {
        const expiredSession = sessionStorage.getItem(SESSION_KEY);
        if (expiredSession) {
          deleteSession(expiredSession).catch(() => {});
        }
        sessionStorage.removeItem(SESSION_KEY);
        chatHistory.set([]);
        const errorText = 'Session expired — a new one will be created automatically.';
        messages.set(prev => [...prev, { role: 'error', text: errorText }]);
        appendErrorBubble(errorText);
      } else {
        const errorText = 'Something went wrong — click retry to resend.';
        messages.set(prev => [...prev, { role: 'error', text: errorText }]);
        appendErrorBubble(errorText);
      }
    } finally {
      loading.set(false);
      setInputEnabled(true);
      hideTyping();
      if (panelOpen.get()) inputEl?.focus();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // ---- Panel open / close / resize ----

  function applyPanelLayout() {
    const w = panelWidth.get();
    panelEl.style.width = w + 'vw';
    panelEl.classList.add('gaia-panel--open');
    triggerEl.classList.add('gaia-robot-trigger--hidden');
    document.body.classList.add('gaia-panel-open');
    const pw = document.getElementById('page-wrapper');
    if (pw) pw.style.marginRight = w + 'vw';
    const topNav = document.querySelector('.navbar-fixed-top');
    if (topNav) topNav.style.right = w + 'vw';
    const bottomNav = document.querySelector('.navbar.fixed-bottom');
    if (bottomNav) bottomNav.style.right = w + 'vw';
  }

  function removePanelLayout() {
    panelEl.classList.remove('gaia-panel--open');
    triggerEl.classList.remove('gaia-robot-trigger--hidden');
    document.body.classList.remove('gaia-panel-open');
    const pw = document.getElementById('page-wrapper');
    if (pw) pw.style.marginRight = '';
    const topNav = document.querySelector('.navbar-fixed-top');
    if (topNav) topNav.style.right = '';
    const bottomNav = document.querySelector('.navbar.fixed-bottom');
    if (bottomNav) bottomNav.style.right = '';
  }

  function openPanel() {
    panelOpen.set(true);
    sessionStorage.setItem(PANEL_OPEN_KEY, 'true');
    applyPanelLayout();
    scrollToBottom();
    inputEl?.focus();
  }

  function closePanel() {
    panelOpen.set(false);
    sessionStorage.setItem(PANEL_OPEN_KEY, 'false');
    removePanelLayout();
    // Update trigger text based on conversation state
    setTriggerText(messages.get().length > 0 ? CONTINUE_MSG : GREETING);
  }

  function initResize(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidthPx = panelEl.getBoundingClientRect().width;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    function onMouseMove(ev) {
      const dx = startX - ev.clientX;
      const newPx = startWidthPx + dx;
      const newPct = Math.min(MAX_PANEL_PCT, Math.max(MIN_PANEL_PCT, (newPx / window.innerWidth) * 100));
      panelWidth.set(Math.round(newPct));
      panelEl.style.width = newPct + 'vw';
      const pw = document.getElementById('page-wrapper');
      if (pw) pw.style.marginRight = newPct + 'vw';
      const topNav = document.querySelector('.navbar-fixed-top');
      if (topNav) topNav.style.right = newPct + 'vw';
      const bottomNav = document.querySelector('.navbar.fixed-bottom');
      if (bottomNav) bottomNav.style.right = newPct + 'vw';
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      sessionStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth.get()));
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  async function handleNewSession() {
    // Delete the old session if it exists
    const oldSession = sessionStorage.getItem(SESSION_KEY);
    if (oldSession) {
      deleteSession(oldSession).catch(() => {});
      sessionStorage.removeItem(SESSION_KEY);
    }
    // Clear local state
    messages.set([]);
    chatHistory.set([]);
    lastFailedText = null;
    sessionStorage.removeItem(MESSAGES_KEY);
    sessionStorage.removeItem(CHAT_HISTORY_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    if (messagesEl) {
      messagesEl.innerHTML = '';
      // Re-add the empty state placeholder
      const placeholder = (
        <div class="gaia-empty-state">
          <i class="fas fa-robot gaia-empty-state__icon" />
          <p class="gaia-empty-state__text">Hey there! I'm rAI — ask me anything.</p>
        </div>
      );
      messagesEl.appendChild(placeholder);
    }
    inputEl?.focus();
  }

  // ---- build the DOM tree ----

  triggerEl = (
    <div class="gaia-robot-trigger">
      <div class="gaia-robot-trigger__icon">
        <i class="fas fa-robot" />
      </div>
      <span
        class="gaia-robot-trigger__text"
        ref={el => {
          triggerTextEl = el;
        }}>
        <span class="gaia-robot-trigger__label" />
      </span>
    </div>
  );
  // Ensure ref is set even if JSX helper doesn't support ref
  if (!triggerTextEl) triggerTextEl = triggerEl.querySelector('.gaia-robot-trigger__text');

  // Stop the bouncing animation after 30 seconds
  setTimeout(() => {
    triggerEl.classList.add('gaia-robot-trigger--settled');
  }, 30000);

  // Keep text bubble within viewport on hover
  triggerEl.addEventListener('mouseenter', () => {
    const textEl = triggerTextEl;
    if (!textEl) return;
    // Reset any previous shift
    textEl.style.left = '50%';
    textEl.style.transform = 'translateX(-50%) scale(1)';
    // Wait a frame so the browser has laid it out
    requestAnimationFrame(() => {
      const rect = textEl.getBoundingClientRect();
      const pad = 8;
      let shiftX = 0;
      if (rect.right > window.innerWidth - pad) {
        shiftX = window.innerWidth - pad - rect.right;
      } else if (rect.left < pad) {
        shiftX = pad - rect.left;
      }
      if (shiftX !== 0) {
        const triggerRect = triggerEl.getBoundingClientRect();
        const triggerCenterX = triggerRect.left + triggerRect.width / 2;
        const newLeft = triggerCenterX - triggerRect.left + shiftX;
        textEl.style.left = newLeft + 'px';
        textEl.style.transform = 'translateX(-50%) scale(1)';
      }
    });
  });

  // ---- Draggable trigger ----
  (function initTriggerDrag() {
    const DRAG_THRESHOLD = 5; // px movement before we consider it a drag
    let dragging = false;
    let startX, startY, startLeft, startTop;

    // Restore saved position or use CSS defaults (top:80px, right:20px)
    const saved = (() => {
      try {
        return JSON.parse(sessionStorage.getItem(TRIGGER_POS_KEY));
      } catch {
        return null;
      }
    })();
    if (saved) {
      triggerEl.style.top = saved.top + 'px';
      triggerEl.style.left = saved.left + 'px';
      triggerEl.style.right = 'auto';
    }

    function pointerDown(e) {
      // Ignore if panel is open
      if (panelOpen.get()) return;
      const pt = e.touches ? e.touches[0] : e;
      const rect = triggerEl.getBoundingClientRect();
      startX = pt.clientX;
      startY = pt.clientY;
      startLeft = rect.left;
      startTop = rect.top;
      dragging = false;

      document.addEventListener('mousemove', pointerMove);
      document.addEventListener('mouseup', pointerUp);
      document.addEventListener('touchmove', pointerMove, { passive: false });
      document.addEventListener('touchend', pointerUp);
    }

    function pointerMove(e) {
      const pt = e.touches ? e.touches[0] : e;
      const dx = pt.clientX - startX;
      const dy = pt.clientY - startY;

      if (!dragging && Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      dragging = true;
      e.preventDefault();

      // Stop float animation while dragging
      triggerEl.classList.add('gaia-robot-trigger--settled');

      let newLeft = startLeft + dx;
      let newTop = startTop + dy;

      // Clamp within viewport (between top and bottom navbars)
      const w = triggerEl.offsetWidth;
      const h = triggerEl.offsetHeight;
      const topBar = document.querySelector('.navbar.fixed-top');
      const bottomBar = document.querySelector('.navbar.fixed-bottom');
      const minTop = topBar ? topBar.offsetHeight : 0;
      const maxBottom = bottomBar ? window.innerHeight - bottomBar.offsetHeight : window.innerHeight;
      newLeft = Math.max(0, Math.min(window.innerWidth - w, newLeft));
      newTop = Math.max(minTop, Math.min(maxBottom - h, newTop));

      triggerEl.style.left = newLeft + 'px';
      triggerEl.style.top = newTop + 'px';
      triggerEl.style.right = 'auto';
    }

    function pointerUp() {
      document.removeEventListener('mousemove', pointerMove);
      document.removeEventListener('mouseup', pointerUp);
      document.removeEventListener('touchmove', pointerMove);
      document.removeEventListener('touchend', pointerUp);

      if (dragging) {
        // Persist position
        try {
          sessionStorage.setItem(
            TRIGGER_POS_KEY,
            JSON.stringify({
              top: parseInt(triggerEl.style.top, 10),
              left: parseInt(triggerEl.style.left, 10),
            }),
          );
        } catch {
          /* non-critical */
        }
      } else {
        openPanel();
      }
    }

    triggerEl.addEventListener('mousedown', pointerDown);
    triggerEl.addEventListener('touchstart', pointerDown, { passive: true });
  })();

  // ---- Set trigger bubble text ----
  function setTriggerText(text) {
    const labelEl = triggerTextEl.querySelector('.gaia-robot-trigger__label');
    labelEl.textContent = text;
    triggerTextEl.classList.toggle('gaia-robot-trigger__text--continue', text === CONTINUE_MSG);
  }

  // Initial greeting — pick message based on whether a conversation exists
  const GREETING = 'Hey there! I\u2019m rAI \u2014 ask me anything.';
  const CONTINUE_MSG = 'Click me to continue our conversation :)';
  setTriggerText(messages.get().length > 0 ? CONTINUE_MSG : GREETING);

  panelEl = (
    <div class="gaia-panel" id="gaia-chat-panel">
      <div class="gaia-panel__resize-handle" onmousedown={initResize} />
      {/* Header */}
      <div class="gaia-panel__header">
        <div class="gaia-header__left">
          <img src={bmwLogoSrc} class="gaia-header__bmw-logo" alt="BMW" />
          <i class="fas fa-robot gaia-header__icon" />
          <span class="gaia-header__title">Chat with rAI</span>
        </div>
        <div class="gaia-header__actions">
          <button
            class="gaia-header__btn gaia-header__info-btn"
            data-tooltip="About rAI"
            onclick={e => {
              e.stopPropagation();
              const box = panelEl.querySelector('.gaia-info-box');
              box.classList.toggle('gaia-info-box--visible');
            }}>
            <i class="fas fa-info-circle" />
          </button>
          <button
            class="gaia-header__btn gaia-header__new-session"
            data-tooltip="New conversation"
            onclick={handleNewSession}>
            <i class="fas fa-trash-alt" />
          </button>
          <button class="gaia-header__btn gaia-panel__close" data-tooltip="Close" onclick={closePanel}>
            <i class="fas fa-times" />
          </button>
        </div>
        <div class="gaia-info-box">
          <p class="gaia-info-box__title">About rAI</p>
          <p class="gaia-info-box__text">
            rAI is powered by <strong>GAIA</strong> — BMW Group's Generative AI platform. Learn more at{' '}
            <a href="https://gaia.bmwgroup.net/genai/" target="_blank" rel="noopener noreferrer">
              gaia.bmwgroup.net
            </a>
            .
          </p>
          <p class="gaia-info-box__disclaimer">
            <i class="fas fa-exclamation-triangle" /> <strong>Disclaimer:</strong> Messages generated by AI can be
            inaccurate. Always verify critical information with the original documents.
          </p>
        </div>
      </div>

      {/* Body (messages + input) */}
      <div class="gaia-body">
        <div class="gaia-messages">
          <div class="gaia-empty-state">
            <i class="fas fa-robot gaia-empty-state__icon" />
            <p class="gaia-empty-state__text">Hey there! I'm rAI — ask me anything.</p>
          </div>
        </div>
        <button class="gaia-scroll-pill" data-tooltip="Jump to latest">
          <i class="fas fa-angles-down" />
        </button>
        <TypingIndicator />
        <div class="gaia-input-bar">
          <textarea class="gaia-input" placeholder="Ask rAI something…" rows="1" onkeydown={handleKeyDown} />
          <button class="gaia-send-btn" data-tooltip="Send" onclick={handleSend}>
            <i class="fas fa-paper-plane" />
          </button>
        </div>
      </div>
    </div>
  );

  // ---- grab refs from the built DOM tree ----
  bodyEl = panelEl.querySelector('.gaia-body');
  messagesEl = panelEl.querySelector('.gaia-messages');
  inputEl = panelEl.querySelector('.gaia-input');
  typingEl = panelEl.querySelector('.gaia-typing');
  sendBtnEl = panelEl.querySelector('.gaia-send-btn');
  sessionBtnEl = panelEl.querySelector('.gaia-header__new-session');
  scrollPillEl = panelEl.querySelector('.gaia-scroll-pill');

  chatRefs.messagesEl = messagesEl;
  chatRefs.scrollPillEl = scrollPillEl;
  chatRefs.typingEl = typingEl;
  chatRefs.inputEl = inputEl;
  chatRefs.sendBtnEl = sendBtnEl;

  // Scroll pill click handler
  if (scrollPillEl) {
    scrollPillEl.addEventListener('click', () => scrollToBottom());
  }

  // Update scroll pill visibility when user scrolls the messages area
  if (messagesEl) {
    messagesEl.addEventListener('scroll', () => updateScrollPill());
  }

  // Click-away to close info box
  const infoBox = panelEl.querySelector('.gaia-info-box');
  const infoBtn = panelEl.querySelector('.gaia-header__info-btn');
  document.addEventListener('click', e => {
    if (!infoBox.classList.contains('gaia-info-box--visible')) return;
    if (infoBox.contains(e.target) || infoBtn.contains(e.target)) return;
    infoBox.classList.remove('gaia-info-box--visible');
  });

  // Typing indicator hidden by default
  hideTyping();

  // ---- Restore previous messages into the DOM ----
  if (savedMessages.length > 0) {
    // Detect old-format error messages (role:'ai' with error text) and migrate them
    const ERROR_PATTERNS = ['something went wrong', 'session expired'];
    const isErrorText = t => ERROR_PATTERNS.some(p => t.toLowerCase().includes(p));
    let migrated = false;

    hideEmptyState();
    savedMessages.forEach(({ role, text, time }, idx) => {
      const isLegacyError = role === 'ai' && isErrorText(text);
      if (role === 'error' || isLegacyError) {
        if (isLegacyError) migrated = true;
        // Only show retry button if this error is the very last message
        const isLastMessage = idx === savedMessages.length - 1;
        let displayText = isLegacyError ? 'Something went wrong \u2014 click retry to resend.' : text;
        // Strip retry CTA from the display text when the button is hidden
        if (!isLastMessage) {
          displayText = displayText.replace(/\s*\u2014\s*click retry to resend\.?/i, '.');
        }
        appendErrorBubble(displayText, isLastMessage);
      } else {
        const bubble = <MessageBubble role={role} text={text} time={time} />;
        messagesEl.appendChild(bubble);
      }
    });

    // If we migrated legacy errors, update the persisted state so next reload is clean
    if (migrated) {
      messages.set(prev =>
        prev.map(m =>
          m.role === 'ai' && isErrorText(m.text)
            ? { role: 'error', text: 'Something went wrong \u2014 click retry to resend.' }
            : m,
        ),
      );
    }

    // If the last saved message was an error (new or legacy), set lastFailedText for retry
    const lastUser = [...savedMessages].reverse().find(m => m.role === 'user');
    const lastEntry = savedMessages[savedMessages.length - 1];
    const lastIsError = lastEntry?.role === 'error' || (lastEntry?.role === 'ai' && isErrorText(lastEntry.text));
    if (lastIsError && lastUser) {
      lastFailedText = lastUser.text;
    }
    scrollToBottom();
  }

  // ---- Resume pending interaction after page reload ----
  const pendingRaw = sessionStorage.getItem(PENDING_KEY);
  if (pendingRaw) {
    try {
      const pending = JSON.parse(pendingRaw);
      resumePendingInteraction(pending);
    } catch {
      sessionStorage.removeItem(PENDING_KEY);
    }
  }

  async function resumePendingInteraction({ sessionId, interactionId, userText, startChatHistory }) {
    loading.set(true);
    setInputEnabled(false);
    showTyping();

    try {
      abortController = new AbortController();
      let streamingStarted = false;
      const updateBubble = appendStreamingBubble();
      const streamingWrapper = messagesEl.lastElementChild;
      streamingWrapper.style.display = 'none';

      const result = await pollInteraction(sessionId, interactionId, abortController.signal, partialText => {
        if (!streamingStarted) {
          streamingStarted = true;
          hideTyping();
          streamingWrapper.style.display = '';
        }
        updateBubble(partialText);
      });

      sessionStorage.removeItem(PENDING_KEY);

      const { replyText, chatHistory: newHistory, isContentFiltered } = handlePollResult(result, startChatHistory);

      if (!streamingStarted) {
        hideTyping();
        streamingWrapper.style.display = '';
      }

      if (isContentFiltered) {
        streamingWrapper.remove();
        // Remove the offending user message bubble from the DOM
        const userBubbles = messagesEl?.querySelectorAll('.gaia-msg--user');
        if (userBubbles?.length) userBubbles[userBubbles.length - 1].remove();
        const blockedText = userText;
        messages.set(prev => {
          const lastUserIdx = prev.findLastIndex(m => m.role === 'user');
          if (lastUserIdx >= 0) return [...prev.slice(0, lastUserIdx), ...prev.slice(lastUserIdx + 1)];
          return prev;
        });
        messages.set(prev => [...prev, { role: 'error', text: replyText }]);
        appendContentFilteredError(replyText, blockedText);
      } else {
        updateBubble(replyText, true);
        messages.set(prev => [...prev, { role: 'ai', text: replyText, time: new Date().toISOString() }]);
      }

      if (newHistory) {
        chatHistory.set(newHistory);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('GAIA resume poll error:', err);
      sessionStorage.removeItem(PENDING_KEY);

      // Remove the streaming bubble left by the failed request (may have partial text)
      const streamBubble = messagesEl?.querySelector('.gaia-msg--ai:last-child');
      if (streamBubble && !streamBubble.classList.contains('gaia-msg--error')) {
        streamBubble.remove();
      }

      lastFailedText = userText;

      if (err.response?.status === 404) {
        sessionStorage.removeItem(SESSION_KEY);
        chatHistory.set([]);
        const errorText = 'Session expired \u2014 a new one will be created automatically.';
        messages.set(prev => [...prev, { role: 'error', text: errorText }]);
        appendErrorBubble(errorText);
      } else {
        const errorText = 'Something went wrong \u2014 click retry to resend.';
        messages.set(prev => [...prev, { role: 'error', text: errorText }]);
        appendErrorBubble(errorText);
      }
    } finally {
      loading.set(false);
      setInputEnabled(true);
      hideTyping();
      if (panelOpen.get()) inputEl?.focus();
    }
  }

  // Auto-resize textarea as user types
  if (inputEl) {
    inputEl.addEventListener('input', () => {
      inputEl.style.height = 'auto';
      inputEl.style.height = Math.min(inputEl.scrollHeight, 80) + 'px';
    });
  }

  // If the panel was previously open, restore the layout instantly (no slide animation)
  if (panelOpen.get()) {
    panelEl.classList.add('gaia-panel--no-transition');
    triggerEl.classList.add('gaia-robot-trigger--no-transition');
    applyPanelLayout();
    // Re-enable transitions after the initial paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panelEl.classList.remove('gaia-panel--no-transition');
        triggerEl.classList.remove('gaia-robot-trigger--no-transition');
      });
    });
  }

  const container = <div class="gaia-chat-container" />;
  container.appendChild(triggerEl);
  container.appendChild(panelEl);
  return container;
}
